/**
 * One table of page metadata, and the schema.org blocks derived from it.
 *
 * ── Why this file exists ──────────────────────────────────────────────────
 * This is a CRA single-page app: one index.html is served for every route,
 * and nothing was setting per-route metadata. All ten public pages therefore
 * shipped the SAME <title> and <meta description> — to a crawler, one page
 * served ten times. robots.txt and sitemap.xml both point Google at all ten,
 * which makes that worse rather than better.
 *
 * The table is the single source of truth, consumed twice:
 *   * useSeo() applies it on client-side navigation, and
 *   * scripts/prerender-seo.js bakes it into static HTML at build time,
 *     because most crawlers that matter for LINK PREVIEWS (Slack, LinkedIn,
 *     iMessage, several AI fetchers) do not execute JavaScript. Googlebot
 *     does; almost nothing else does.
 *
 * ── Writing the copy ──────────────────────────────────────────────────────
 * Titles are ~55 characters so Google does not truncate them, and lead with
 * the phrase somebody would actually type — "ATS resume checker", not
 * "NextOpp Features". Descriptions are ~155 characters and each one makes a
 * concrete, checkable promise rather than describing the page.
 *
 * Every claim here is one the product can keep. The free scan really is
 * account-free, the tailoring really is grounded, and nothing is auto-
 * submitted — writing marketing copy this product cannot honour would be a
 * strange way to sell honesty.
 */

import { FAQ_PAIRS } from "./faq-data.js";
import { ATS_FAQ } from "./ats-content.js";
import { GUIDES } from "./guides.js";
import { FEATURES } from "./product-tour.js";
import { CRITERIA } from "./compare.js";

/** The free checker's canonical path.
 *
 * Exported because it moved once — from /scan to a URL carrying the phrase
 * people search for — and five tests broke on the literal. One name to change
 * next time. /scan still redirects here, so old links keep working. */
export const SCAN_PATH = "/ats-resume-checker";

export const SITE = {
  name: "NextOpp.io",
  url: "https://nextopp.io",
  ogImage: "https://nextopp.io/og-image.png",
  twitter: "summary_large_image",
};

const t = (title, description, extra = {}) => ({ title, description, ...extra });

export const PAGES = {
  "/": t(
    "NextOpp.io — Tailor Your Real Resume to Any Job",
    "Score your resume against any job in seconds, then tailor it from your real history — never invented. Free ATS scan, no account needed.",
    { schema: ["organization", "software"] }
  ),
  "/ats-resume-checker": t(
    "Free ATS Resume Checker — Score Your Resume vs Any Job",
    "Paste your resume and a job description for a free 0–100 ATS match score and the exact keywords you are missing. No account, no card, nothing stored.",
    { schema: ["howto-scan", "ats-faq"] }
  ),
  "/pricing": t(
    "Pricing — Start Free, No Card | NextOpp.io",
    "A free plan that is a real plan, not a countdown: base resume, tailored resumes, cover letters and a pipeline tracker. Upgrade only when you are applying at volume.",
    { schema: ["software"] }
  ),
  "/features": t(
    "Features — ATS Scoring, Resume Tailoring & Job Radar",
    "ATS match scoring, truthful resume tailoring, cover letters in five tones, interview prep, and Job Radar reading company career pages directly.",
    { schema: ["software"] }
  ),
  "/early": t(
    "First Job, Internship & Summer Work Search for Students",
    "Find first jobs, internships, apprenticeships and seasonal work on employers' own boards, filtered by the age they take. Build a first resume from your own answers.",
  ),
  "/government": t(
    "Government Jobs — Federal, State & Local Postings in One Search",
    "Federal announcements from USAJOBS with closing dates and pay grades, plus state, county and city postings from their own boards. Tailor a truthful resume to each.",
  ),
  "/compare": t(
    "How to Choose an ATS Resume Tool — 7 Questions to Ask",
    "Seven questions worth asking any resume tool, including this one: does it invent, does it auto-apply, is the free tier real, and does it show its working?",
    { schema: ["compare-faq"] }
  ),
  "/guides": t(
    "Job Search Guides — ATS, Keywords & Resume Format",
    "Plain explanations of applicant tracking systems, resume keywords and the formatting that quietly loses your text. Written from running a parser, not from folklore.",
  ),
  "/use-cases": t(
    "Use Cases — For Career Switchers, Senior Roles & Coaches",
    "However you job search, do it with craft: high-volume applicants, career switchers, senior roles where precision matters, and career coaches.",
  ),
  "/faq": t(
    "FAQ — Does NextOpp Invent Experience? (No) | NextOpp.io",
    "Straight answers on how the AI works, what it will not do, file types, ATS scoring, pricing and your data. It never invents experience and never auto-applies.",
    { schema: ["faq"] }
  ),
  "/security": t(
    "Security & Privacy — How We Handle Your Resume",
    "Your resume is private storage, never a public URL. You approve every AI action, we log content-minimally, and you can delete everything at any time.",
  ),
  "/demo": t(
    "Live Demo — Watch a Resume Go From 68 to 89",
    "A full walkthrough on a fictional persona: ATS score, keyword gaps, a truthful rewrite, and the tracker. No signup, every number from the real engine.",
  ),
  "/contact": t(
    "Contact NextOpp.io — Talk to a Real Person",
    "Questions, feedback, bugs or partnership ideas. We read everything and reply, usually within one business day.",
  ),
  "/privacy": t("Privacy Policy | NextOpp.io",
    "What we collect, why, how long we keep it, and how to delete it. Your resume is never exposed publicly and is never sold."),
  "/terms": t("Terms of Service | NextOpp.io",
    "The agreement covering your use of NextOpp.io, including the human-in-the-loop rules and what the AI will and will not do on your behalf."),
  "/login": t("Sign in | NextOpp.io", "Sign in to your NextOpp.io account.", { noindex: true }),
  "/signup": t("Create a free account | NextOpp.io",
    "Free to start, no credit card. Tailor your first application in minutes.", { noindex: true }),
};

// Generated rather than hand-written: a guide added to guides.js gets its
// title, description, Article and BreadcrumbList automatically, and the copy
// cannot drift from the page because there is only one copy.
// Same reasoning as the guides below: one description of a feature, rendered
// as the page and as its metadata, so the two cannot drift.
PAGES["/docs"] = t(
  "Documentation — Every Feature, and What It Won't Do",
  "One page per feature: what it changes for you, the steps to use it, and the limits stated up front. It never invents experience and never submits an application.",
);
FEATURES.forEach((f) => {
  PAGES[`/docs/${f.slug}`] = {
    title: `${f.name} — NextOpp.io Documentation`,
    description: f.value.slice(0, 155),
  };
});

GUIDES.forEach((g) => {
  PAGES[`/guides/${g.slug}`] = {
    title: g.title,
    description: g.description,
    schema: [`guide:${g.slug}`],
  };
});

export const DEFAULT_PAGE = PAGES["/"];

/** Metadata for a path, falling back to the homepage's. */
export function pageMeta(pathname) {
  return PAGES[pathname] || PAGES[(pathname || "").replace(/\/+$/, "")] || DEFAULT_PAGE;
}

// ── schema.org ────────────────────────────────────────────────────────────
// AI summarizers parse structured data far more reliably than they parse a
// marketing layout, and it is the same block that earns rich results. Kept in
// this file so a claim cannot drift between the visible page and the machine-
// readable copy of it.

const organization = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/app-icon.png`,
  description:
    "AI job search that tailors your real resume to each role — grounded in your actual experience, with you approving every step.",
});

const software = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: SITE.url,
  description:
    "ATS match scoring, truthful resume tailoring, cover letters, interview prep and application tracking.",
  // A real free tier, stated as one. Marking a trial as price 0 is the common
  // way this property becomes a lie.
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free plan — no credit card required",
  },
});

const howToScan = () => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to check your resume against a job description",
  description:
    "Get a 0–100 ATS match score and the keywords you are missing, without an account.",
  step: [
    { "@type": "HowToStep", name: "Paste your resume", text: "Paste the full text of your resume into the first field." },
    { "@type": "HowToStep", name: "Paste the job description", text: "Paste the posting you want to be measured against." },
    { "@type": "HowToStep", name: "Read the gaps", text: "You get a match score, the keywords you already hit, and the ones you are missing." },
  ],
});

/** FAQPage built from the questions the FAQ page actually renders. */
export function faqSchema(pairs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (pairs || []).map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

const faqPage = () => faqSchema(FAQ_PAIRS);

// Its own FAQPage rather than a slice of the main one: these questions are
// about ATS scoring specifically, which is what that page is trying to rank
// for, and a rich result should answer the query it appeared under.
const atsFaqPage = () => faqSchema(ATS_FAQ);

/** Article + breadcrumbs + the guide's own questions. */
function guideSchema(slug) {
  const g = GUIDES.find((x) => x.slug === slug);
  if (!g) return [];
  const url = `${SITE.url}/guides/${g.slug}`;
  const blocks = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: g.heading,
      description: g.description,
      // datePublished is the same as dateModified until a guide is revised.
      // Inventing an earlier publication date to look established is the
      // cheapest possible lie and the easiest to catch.
      datePublished: g.updated,
      dateModified: g.updated,
      mainEntityOfPage: url,
      author: { "@type": "Organization", name: SITE.name, url: SITE.url },
      publisher: {
        "@type": "Organization",
        name: SITE.name,
        logo: { "@type": "ImageObject", url: `${SITE.url}/app-icon.png` },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Guides", item: `${SITE.url}/guides` },
        { "@type": "ListItem", position: 2, name: g.heading, item: url },
      ],
    },
  ];
  if (g.faq?.length) blocks.push(faqSchema(g.faq));
  return blocks;
}

// The criteria are already question-and-answer shaped, which is what FAQPage
// wants — and what an AI summarizer answering "is X any good?" can quote.
const compareFaq = () => faqSchema(CRITERIA.map((c) => [c.question, c.ours]));

const BUILDERS = {
  organization, software, "howto-scan": howToScan, "compare-faq": compareFaq,
  faq: faqPage, "ats-faq": atsFaqPage,
};

/** The static schema blocks for a path. FAQ is passed in by the page. */
export function schemaFor(pathname) {
  const meta = pageMeta(pathname);
  return (meta.schema || []).flatMap((key) => {
    if (key.startsWith("guide:")) return guideSchema(key.slice(6));
    const fn = BUILDERS[key];
    return fn ? [fn()] : [];
  });
}


/**
 * The sitemap, derived from the table rather than maintained beside it.
 *
 * public/sitemap.xml was hand-written, so adding a page meant remembering to
 * edit two files — and the one you forget is the one nobody notices, because a
 * missing sitemap entry has no symptom. /ats-resume-checker, the highest-intent
 * page on the site, was absent from it for exactly that reason.
 *
 * Generated at build time by scripts/prerender-seo.mjs. noindex pages are
 * excluded: submitting a URL you have told Google not to index is a
 * contradiction Search Console reports as an error.
 */
export function buildSitemap(lastmod = new Date().toISOString().slice(0, 10)) {
  // Crawl priority is about intent, not site structure: the free checker earns
  // 1.0 alongside the homepage because it is the page that answers a search.
  const PRIORITY = {
    "/": ["1.0", "weekly"],
    "/ats-resume-checker": ["1.0", "weekly"],
    "/pricing": ["0.9", "weekly"],
    "/features": ["0.9", "monthly"],
    "/guides": ["0.8", "weekly"],
    "/docs": ["0.8", "weekly"],
    "/faq": ["0.8", "monthly"],
  };
  const rows = Object.entries(PAGES)
    .filter(([, m]) => !m.noindex)
    .map(([route]) => {
      const [priority, changefreq] =
        PRIORITY[route] || (route.startsWith("/guides/") ? ["0.7", "monthly"] : ["0.5", "monthly"]);
      const loc = `${SITE.url}${route === "/" ? "/" : route}`;
      return `  <url><loc>${loc}</loc><lastmod>${lastmod}</lastmod>` +
             `<changefreq>${changefreq}</changefreq><priority>${priority}</priority></url>`;
    });
  return `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<!-- Generated from src/lib/seo.js by scripts/prerender-seo.mjs. Do not edit by hand. -->\n` +
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${rows.join("\n")}\n</urlset>\n`;
}
